/** Dialog to create a new form when the student is fixed (e.g. from student page). User selects enrollment period and form template. */
import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import * as React from "react";
import { InfoAlert } from "src/components/Feedback/InfoAlert";
import { EligibleFormTemplateSelectInput } from "src/components/Inputs/EligibleFormTemplateSelectInput";
import { EnrollmentPeriodSelectInput } from "src/components/Inputs/EnrollmentPeriodSelectInput";
import { PersonSelectInput } from "src/components/Inputs/PersonSelectInput";
import type {
  ApplicantSummary,
  EligibleFormTemplateSummary,
} from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { validateWithZod } from "src/services/formValidations";
import * as GQL from "src/types/graphql";
import { GET_STUDENT_BY_ID_ADMIN_PORTAL } from "../../students/graphql/queries";
import {
  CreateFormFormSchema,
  CreateFormFormValues,
  CreateFormSelectedApplicant,
  CreateFormSelectedEnrollmentPeriod,
  CreateFormSelectedTemplate,
  useCreateFormSubmit,
} from "./useCreateFormSubmit";

interface CreateFormForStudentDialogProps {
  applicantId: uuid;
  enrollmentPeriodId?: uuid;
  isOpen: boolean;
  onClose: () => void;
  width?: string;
  height?: string;
}

export function CreateFormForStudentDialog({
  applicantId,
  enrollmentPeriodId,
  isOpen,
  onClose,
  width,
  height,
}: CreateFormForStudentDialogProps) {
  const organization = useOrganization();
  const organizationId = React.useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );

  const [selectedFormTemplate, setSelectedFormTemplate] =
    React.useState<CreateFormSelectedTemplate>(undefined);
  const [selectedApplicant, setSelectedApplicant] =
    React.useState<CreateFormSelectedApplicant>(undefined);
  const [selectedEnrollmentPeriod, setSelectedEnrollmentPeriod] =
    React.useState<CreateFormSelectedEnrollmentPeriod>(
      enrollmentPeriodId ? { id: enrollmentPeriodId } : undefined
    );

  const { remoteData: studentRd } = useRemoteDataQuery<
    GQL.GetStudentByIdAdminPortal,
    GQL.GetStudentByIdAdminPortalVariables
  >(GET_STUDENT_BY_ID_ADMIN_PORTAL, {
    variables: {
      student_id: applicantId,
      organization_id: organizationId,
    },
    skip: !applicantId || !isOpen || !organizationId,
  });

  React.useEffect(() => {
    if (studentRd.hasData() && studentRd.data.person_by_pk) {
      const p = studentRd.data.person_by_pk;
      const summary: ApplicantSummary = {
        id: p.id,
        first_name: p.first_name ?? "",
        middle_name: p.middle_name ?? null,
        last_name: p.last_name ?? "",
        birth_date: p.birth_date ?? null,
        reference_id: p.reference_id ?? null,
      };
      setSelectedApplicant(summary);
    }
  }, [studentRd]);

  const handleClose = React.useCallback(
    (resetForm: () => void) => {
      resetForm();
      setSelectedFormTemplate(null);
      setSelectedApplicant(null);
      onClose();
    },
    [onClose]
  );

  const initialValues: CreateFormFormValues = React.useMemo(
    () => ({
      organizationId,
      enrollmentPeriodId: enrollmentPeriodId || "",
      formTemplateId: "",
      applicantId,
    }),
    [organizationId, enrollmentPeriodId, applicantId]
  );

  const onSelectedFormTemplate = React.useCallback(
    (formTemplate: EligibleFormTemplateSummary | null | undefined) => {
      setSelectedFormTemplate(formTemplate);
    },
    []
  );

  const onSelectedEnrollmentPeriod = React.useCallback(
    (period: CreateFormSelectedEnrollmentPeriod) => {
      setSelectedEnrollmentPeriod(period ?? undefined);
    },
    []
  );

  const {
    handleSubmitForm,
    getAssignedFormsRD,
    alertText,
    alertStatus,
    showSeeFormLink,
    canStartForm,
    handleOnSeeFormClick,
    isSubmitting,
  } = useCreateFormSubmit({
    selectedEnrollmentPeriod,
    selectedApplicant,
    selectedFormTemplate,
    onClose: handleClose,
  });

  return (
    <Formik<CreateFormFormValues>
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmitForm}
      validate={validateWithZod(CreateFormFormSchema)}
      validateOnBlur
      validateOnMount
      validateOnChange
    >
      {({ values, isValid, resetForm }) => {
        const handleOnClose = () => handleClose(resetForm);
        return (
          <Modal isCentered isOpen={isOpen} onClose={handleOnClose}>
            <ModalOverlay />
            <ModalContent width={width} height={height}>
              <Form noValidate>
                <ModalHeader>Create new form</ModalHeader>

                <ModalBody as={VStack} padding={6} pb={4}>
                  <Flex direction="column" gap={4} width="100%">
                    <PersonSelectInput
                      name="applicantId"
                      organizationId={organizationId}
                      personType="applicant"
                      onSelectedPerson={() => {}}
                      isRequired
                      isDisabled
                    />

                    <EnrollmentPeriodSelectInput
                      name="enrollmentPeriodId"
                      organizationId={organizationId}
                      isRequired
                      placeholder="Select enrollment period"
                      label="Enrollment period"
                      onSelectedEnrollmentPeriod={onSelectedEnrollmentPeriod}
                    />

                    {values.applicantId && values.enrollmentPeriodId && (
                      <EligibleFormTemplateSelectInput
                        name="formTemplateId"
                        applicantId={values.applicantId}
                        enrollmentPeriodId={values.enrollmentPeriodId}
                        onSelectedFormTemplate={onSelectedFormTemplate}
                        isRequired
                      />
                    )}

                    {alertText && (
                      <InfoAlert status={alertStatus}>
                        <Text fontSize="md">
                          {alertText}
                          {showSeeFormLink && (
                            <Link
                              variant="link"
                              onClick={() =>
                                handleOnSeeFormClick(handleOnClose)
                              }
                              color="blue.900"
                              fontWeight="700"
                              fontSize="md"
                              textDecoration="underline"
                              paddingLeft="2"
                            >
                              See form.
                            </Link>
                          )}
                        </Text>
                      </InfoAlert>
                    )}
                  </Flex>
                </ModalBody>

                <ModalFooter gap={2} pt={2} pb={4}>
                  <Button
                    onClick={() => {
                      amplitude.track("Cancel create form clicked");
                      handleOnClose();
                    }}
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={getAssignedFormsRD.isLoading() || isSubmitting}
                    isDisabled={!canStartForm || !isValid}
                    onClick={() => amplitude.track("Create form clicked")}
                  >
                    Create
                  </Button>
                </ModalFooter>
              </Form>
            </ModalContent>
          </Modal>
        );
      }}
    </Formik>
  );
}
